/**
 * Dashboard API Service
 * Provides data for the dashboard, switching between mock and real APIs
 * based on the USE_MOCK_DATA environment variable
 */

import { USE_MOCK_DATA, mockApiService } from './mockData';
import api from './api';

/**
 * Dashboard API that automatically uses mock or real data
 */
export const dashboardApi = {
  /**
   * Get pricing health metrics for the last 30 days
   */
  getPricingHealth: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.getPricingHealth();
    }
    // Real API call - endpoint to be implemented
    return api.get('/dashboard/pricing-health');
  },

  /**
   * Get recent estimates
   * @param {number} limit - Maximum number of estimates to return
   */
  getRecentEstimates: async (limit = 6) => {
    if (USE_MOCK_DATA) {
      return mockApiService.getRecentEstimates(limit);
    }
    return api.get(`/estimates?limit=${limit}&sort=estimateDate:desc`);
  },

  /**
   * Get pricing signals and warnings
   */
  getPricingSignals: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.getPricingSignals();
    }
    return api.get('/dashboard/signals');
  },

  /**
   * Get recently referenced historical jobs
   * @param {number} limit - Maximum number of jobs to return
   */
  getReferencedJobs: async (limit = 5) => {
    if (USE_MOCK_DATA) {
      return mockApiService.getReferencedJobs(limit);
    }
    return api.get(`/jobs?limit=${limit}&sort=timesReferenced:desc`);
  },

  /**
   * Get user onboarding state
   */
  getOnboardingState: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.getOnboardingState();
    }
    return api.get('/user/onboarding');
  },

  /**
   * Get dashboard summary stats
   */
  getDashboardStats: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.getDashboardStats();
    }
    return api.get('/dashboard/stats');
  },

  /**
   * Get all dashboard data in one call (more efficient)
   */
  getDashboardData: async () => {
    if (USE_MOCK_DATA) {
      return mockApiService.getDashboardData();
    }

    // Helper to safely call an API and return null on failure
    const safe = (promise) => promise.catch((err) => {
      console.warn('Dashboard API call failed:', err.message);
      return null;
    });

    // Unwrap response: APIs return either { data: ... } (success_response)
    // or raw objects like { estimates: [...] } (list endpoints)
    const unwrap = (res) => res?.data ?? res;

    const [pricingHealth, recentEstimates, pricingSignals, referencedJobs, onboardingState, stats] =
      await Promise.all([
        safe(dashboardApi.getPricingHealth()),
        safe(dashboardApi.getRecentEstimates()),
        safe(dashboardApi.getPricingSignals()),
        safe(dashboardApi.getReferencedJobs()),
        safe(dashboardApi.getOnboardingState()),
        safe(dashboardApi.getDashboardStats()),
      ]);

    return {
      pricingHealth: unwrap(pricingHealth),
      recentEstimates: unwrap(recentEstimates)?.estimates || [],
      pricingSignals: unwrap(pricingSignals)?.signals || [],
      referencedJobs: unwrap(referencedJobs)?.jobs || [],
      onboardingState: unwrap(onboardingState),
      stats: unwrap(stats),
    };
  },

  /**
   * Update onboarding step completion
   * @param {string} step - The onboarding step to mark complete
   */
  completeOnboardingStep: async (step) => {
    if (USE_MOCK_DATA) {
      // In mock mode, just return success
      return { success: true, step };
    }
    return api.post('/user/onboarding/complete', { step });
  },

  /**
   * Set the tenant's business type (and derived defaultVocabularyMode).
   * Tenant-admin only on the backend.
   */
  setBusinessType: async (businessType, defaultVocabularyMode = null) => {
    if (USE_MOCK_DATA) {
      return { success: true, businessType, defaultVocabularyMode };
    }
    const body = { business_type: businessType };
    if (defaultVocabularyMode) {
      body.default_vocabulary_mode = defaultVocabularyMode;
    }
    return api.put('/user/onboarding/business-type', body);
  },
};

export default dashboardApi;
